import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, dashboardPathFor } from "@/contexts/AuthContext";
import SolimoesLogo from "@/components/SolimoesLogo";
import { toast } from "sonner";
import { formatCPF, formatCNPJ } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [personType, setPersonType] = useState("PF");
  const [role, setRole] = useState(params.get("role") || "cliente");
  const [plan, setPlan] = useState(params.get("plan") || "gold");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [doc, setDoc] = useState("");
  const [referralCode, setReferralCode] = useState((params.get("ref") || "").toUpperCase());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (params.get("plan") === "corporativo") setPersonType("PJ");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await register({
      name, email, password, phone,
      person_type: personType,
      document: doc,
      role,
      plan,
      company_name: personType === "PJ" ? companyName || name : undefined,
      referral_code: referralCode ? referralCode.trim().toUpperCase() : undefined,
    });
    setBusy(false);
    if (!r.ok) { toast.error(r.error || "Erro ao cadastrar"); return; }
    toast.success("Cadastro realizado com sucesso!");
    nav(dashboardPathFor(r.user.role), { replace: true });
  };

  const docMask = personType === "PF" ? formatCPF : formatCNPJ;

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10 px-4" data-testid="register-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/"><SolimoesLogo className="h-12 w-auto" /></Link>
        <div className="card-soft mt-6 p-8 lg:p-10">
          <h1 className="font-display text-3xl lg:text-4xl font-black text-[#0E2A5C]">Solicitar meu cartão</h1>
          <p className="mt-2 text-slate-600">Cadastro rápido. Sem carência. Sem burocracia.</p>

          {/* Type tabs */}
          <div className="mt-6 grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-full" data-testid="person-type-toggle">
            {["PF", "PJ"].map(t => (
              <button key={t} type="button" onClick={() => setPersonType(t)}
                      className={`py-2 rounded-full text-sm font-semibold transition ${personType===t ? "bg-white shadow text-[#0E2A5C]" : "text-slate-500"}`}
                      data-testid={`person-type-${t}`}>
                {t === "PF" ? "Pessoa Física" : "Pessoa Jurídica"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="register-form">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1.5">Eu sou</label>
              <select value={role} onChange={(e)=>setRole(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20" data-testid="register-role">
                <option value="cliente">Cliente — quero o cartão</option>
                <option value="credenciado">Credenciado — quero atender</option>
                <option value="vendedor">Vendedor — quero vender</option>
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1.5">{personType === "PF" ? "Nome completo" : "Nome do responsável"}</label>
              <input required value={name} onChange={(e)=>setName(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20" data-testid="register-name" />
            </div>

            {personType === "PJ" && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-1.5">Razão social / Nome fantasia</label>
                <input value={companyName} onChange={(e)=>setCompanyName(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20" data-testid="register-company" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5">{personType === "PF" ? "CPF" : "CNPJ"}</label>
              <input required value={docMask(doc)} onChange={(e)=>setDoc(e.target.value.replace(/\D/g,""))} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20" data-testid="register-document" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Telefone</label>
              <input value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="(00) 00000-0000" className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20" data-testid="register-phone" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">E-mail</label>
              <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20" data-testid="register-email" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Senha</label>
              <input type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20" data-testid="register-password" />
            </div>

            {role === "cliente" && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-1.5">Plano desejado</label>
                <div className="grid grid-cols-3 gap-2">
                  {["gold","diamante","corporativo"].map(p => (
                    <button key={p} type="button" onClick={()=>setPlan(p)}
                            className={`py-3 rounded-xl border text-sm font-semibold transition ${plan===p ? "border-[#2A8FE0] bg-blue-50 text-[#0E2A5C]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                            data-testid={`register-plan-${p}`}>
                      {p === "gold" ? "Gold" : p === "diamante" ? "Diamante" : "Corporativo"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {role === "cliente" && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-1.5">
                  Código de indicação <span className="text-slate-400 font-normal">(opcional)</span>
                </label>
                <input
                  value={referralCode}
                  onChange={(e)=>setReferralCode(e.target.value.toUpperCase())}
                  maxLength={8}
                  placeholder="Ex.: ABCD1234"
                  className="w-full font-mono tracking-widest rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20"
                  data-testid="register-referral-code"
                />
                {referralCode && (
                  <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid="referral-applied-banner">
                    🎁 Você foi indicado! Quem te indicou ganhará 10% da sua primeira fatura como cashback.
                  </div>
                )}
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-primary sm:col-span-2 mt-2 disabled:opacity-50" data-testid="register-submit">
              {busy ? "Cadastrando..." : "Criar minha conta"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-600">Já tem conta? <Link to="/entrar" className="text-[#2A8FE0] font-semibold hover:underline" data-testid="register-to-login">Entrar</Link></p>
        </div>
      </div>
    </div>
  );
}
